import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { colors, Container, Button, Card, Section } from '../styles/GlobalStyles';
import { backendApiService } from '../services/backendApiService';
import logo from '../assets/logo-2026.png';
import dishImage from '../assets/bavarian-dish.jpg';

const glassImage = '/bicchiere_commemorativo_26.png';

const HeroSection = styled(Section)`
  background: ${colors.cream};
  text-align: center;
  padding: 110px 0 90px;

  @media (max-width: 768px) {
    padding: 70px 0 60px;
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LogoImage = styled(motion.img)`
  width: 220px;
  height: 220px;
  margin-bottom: 36px;

  @media (max-width: 768px) {
    width: 160px;
    height: 160px;
    margin-bottom: 24px;
  }
`;

const Title = styled(motion.h1)`
  font-size: 3rem;
  font-weight: 600;
  margin: 0 0 18px 0;

  @media (max-width: 768px) {
    font-size: 2.1rem;
  }
`;

const Subtitle = styled(motion.h2)`
  font-size: 1.2rem;
  font-weight: 300;
  color: ${colors.lightBrown};
  max-width: 560px;
  margin: 0 0 32px 0;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Divider = styled.div`
  width: 60px;
  height: 1px;
  background: ${colors.primary};
  margin: 0 0 32px 0;
`;

const EventDate = styled(motion.div)`
  font-family: 'Playfair Display', serif;
  font-size: 1.25rem;
  color: ${colors.brown};
  margin-bottom: 40px;
  line-height: 1.8;

  @media (max-width: 768px) {
    font-size: 1.05rem;
  }
`;

const Tagline = styled(motion.p)`
  font-size: 1.05rem;
  color: ${colors.lightBrown};
  max-width: 480px;
  margin: -10px 0 36px 0;
`;

const InfoSection = styled(Section)`
  background: ${colors.white};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-bottom: 50px;
`;

const InfoCard = styled(Card)`
  text-align: center;

  h3 {
    font-size: 1.4rem;
    margin-bottom: 12px;
  }

  p {
    color: ${colors.lightBrown};
  }
`;

const Kicker = styled.div`
  font-family: 'Roboto', sans-serif;
  font-size: 0.8rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${colors.primary};
  margin-bottom: 12px;
`;

const MenuSection = styled(Section)`
  background: ${colors.cream};
`;

const MenuTitle = styled.h2`
  text-align: center;
  font-size: 2.2rem;
  margin-bottom: 40px;
`;

const MenuCard = styled(Card)`
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
`;

const DishImageWrapper = styled(motion.div)`
  width: 100%;
  height: 460px;
  overflow: hidden;
  margin-bottom: 50px;

  @media (max-width: 768px) {
    height: 260px;
    margin-bottom: 35px;
  }
`;

const DishImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
`;

const MenuCategory = styled.div`
  margin-bottom: 30px;

  h3 {
    color: ${colors.brown};
    font-size: 1.2rem;
    margin-bottom: 15px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  p {
    font-size: 1.05rem;
    line-height: 1.8;
    color: ${colors.lightBrown};
  }
`;

const PriceBox = styled.div`
  margin-top: 30px;
  padding: 20px;
  background: ${colors.cream};
  border: 1px solid ${colors.secondary};
  text-align: center;

  p {
    margin: 5px 0;
    color: ${colors.brown};
  }
`;

const SouvenirRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 28px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${colors.secondary};

  @media (max-width: 560px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const SouvenirImage = styled.img`
  height: 140px;
  width: auto;
  flex-shrink: 0;
`;

const SouvenirText = styled.div`
  text-align: left;

  h4 {
    font-size: 1.15rem;
    color: ${colors.brown};
    margin: 0 0 4px 0;
  }

  p {
    font-size: 0.95rem;
    color: ${colors.lightBrown};
    margin: 0;
  }

  strong {
    color: ${colors.primary};
  }

  @media (max-width: 560px) {
    text-align: center;
  }
`;

const ActionSection = styled(Section)`
  background: ${colors.white};
  text-align: center;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const CharityInfo = styled(motion.div)`
  background: ${colors.cream};
  padding: 30px;
  border: 1px solid ${colors.secondary};
  margin-top: 40px;

  h3 {
    margin-bottom: 15px;
  }

  p {
    font-size: 1.1rem;
    color: ${colors.brown};
  }
`;

interface HomePageProps {
  onRegisterClick: () => void;
  onShowClosureMessage: (message: string) => void;
}

export const HomePage: React.FC<HomePageProps> = ({ onRegisterClick, onShowClosureMessage }) => {
  const [checking, setChecking] = useState(false);

  const handleRegisterClick = async () => {
    if (checking) return;

    try {
      setChecking(true);
      console.log('🔍 Controllo stato iscrizioni prima di aprire il form...');

      const status = await backendApiService.checkRegistrationStatus();

      if (status.open) {
        console.log('✅ Iscrizioni aperte, apertura form...');
        onRegisterClick();
      } else {
        console.log('🚫 Iscrizioni chiuse:', status.message);
        onShowClosureMessage(status.message);
      }
    } catch (error) {
      console.error('❌ Errore nel controllo delle iscrizioni:', error);
      // In caso di errore, permettiamo l'accesso al form
      onRegisterClick();
    } finally {
      setChecking(false);
    }
  };

  const handleDownloadFlyer = () => {
    // Scarica il file aria_di_fest.jpg dalla cartella public
    const link = document.createElement('a');
    link.href = '/aria_di_fest_26.jpg';
    link.download = 'Aria_di_Fest_Locandina.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShareFlyer = async () => {
    const shareData = {
      title: 'Aria di Fest - Cena Benefica',
      text: 'Partecipa alla cena benefica per la ricerca sulla Fibrosi Cistica! 24 Ottobre 2026',
      url: window.location.href,
    };

    // Se supporta la Web Share API (principalmente mobile)
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.log('Condivisione annullata');
      }
    } else {
      // Fallback per desktop: copia negli appunti
      try {
        await navigator.clipboard.writeText(
          `Aria di Fest - Cena Benefica\n\nPartecipa alla cena benefica per la ricerca sulla Fibrosi Cistica!\n24 Ottobre 2026\n\n${window.location.href}`
        );

        // Mostra una notifica temporanea
        const notification = document.createElement('div');
        notification.innerHTML = '✅ Link copiato negli appunti!';
        notification.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: ${colors.accent};
          color: white;
          padding: 15px 20px;
          border-radius: 2px;
          z-index: 10000;
          font-weight: bold;
          animation: slideIn 0.3s ease-out;
        `;

        // Aggiungi animazione CSS
        const style = document.createElement('style');
        style.textContent = `
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `;
        document.head.appendChild(style);
        document.body.appendChild(notification);

        // Rimuovi la notifica dopo 3 secondi
        setTimeout(() => {
          notification.remove();
          style.remove();
        }, 3000);

      } catch (error) {
        alert('Link copiato negli appunti:\n' + window.location.href);
      }
    }
  };

  return (
    <>
      <HeroSection>
        <Container>
          <HeroContent>
            <LogoImage
              src={logo}
              alt="Aria di Fest 2026"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
            />
            <Title
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
            >
              Aria di Fest 2026
            </Title>
            <Subtitle
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              Una cena privata benefica in stile bavarese per sostenere la ricerca sulla Fibrosi Cistica
            </Subtitle>
            <Divider />
            <EventDate
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              24 Ottobre 2026 · Ore 20:00<br />
              Buffet del Pellegrino — Via Piave, 2, Seregno
            </EventDate>
            <Tagline
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              Un'occasione per stare insieme, gustare sapori autentici e sostenere la ricerca
            </Tagline>
            <Button onClick={handleRegisterClick} disabled={checking}>
              {checking ? 'Controllo...' : 'Iscriviti ora'}
            </Button>
          </HeroContent>
        </Container>
      </HeroSection>

      <InfoSection>
        <Container>
          <InfoGrid>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <InfoCard>
                <Kicker>La serata</Kicker>
                <h3>Evento Speciale</h3>
                <p>
                  Una serata magica in stile bavarese con musica, atmosfera autentica
                  e la possibilità di fare del bene per la ricerca scientifica.
                </p>
              </InfoCard>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <InfoCard>
                <Kicker>La cucina</Kicker>
                <h3>Cucina Bavarese</h3>
                <p>
                  Menù tradizionale con specialità tedesche autentiche,
                  preparate con ingredienti di alta qualità per un'esperienza culinaria unica.
                </p>
              </InfoCard>
            </motion.div>
          </InfoGrid>
        </Container>
      </InfoSection>

      <MenuSection>
        <DishImageWrapper
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <DishImage src={dishImage} alt="Specialità bavaresi: würstel, brezel e birra" />
        </DishImageWrapper>
        <Container>
          <MenuTitle>I Menù della serata</MenuTitle>
          <MenuCard>
            <MenuCategory>
              <h3>Menù Adulto</h3>
              <p><strong>Antipasti:</strong> Speck • Cetriolini • Weisswurst • Brezel</p>
              <p><strong>Secondi:</strong> Stinco con Rösti di patate o Wiener Schnitzel con Rösti di patate <small style={{ color: colors.lightBrown }}>(da scegliere)</small></p>
              <p><strong>Dolci:</strong> Strudel con gelato</p>
            </MenuCategory>

            <MenuCategory>
              <h3>Menù Bambino</h3>
              <p><strong>Primi:</strong> Pasta al sugo</p>
              <p><strong>Secondi:</strong> Wiener Schnitzel con Rösti di patate</p>
              <p><strong>Dolci:</strong> Strudel con gelato</p>
            </MenuCategory>

            <MenuCategory>
              <h3>Menù Vegetariano</h3>
              <p><strong>Antipasti:</strong> Tortino salato • Cetriolini • Crauti crudi • Brezel</p>
              <p><strong>Secondi:</strong> Hamburger di verdure, formaggio, pomodori, insalata con contorno di Rösti di patate</p>
              <p><strong>Dolci:</strong> Strudel con gelato</p>
            </MenuCategory>

            <p style={{ color: colors.lightBrown }}><strong style={{ color: colors.brown }}>Incluso in tutti i menù:</strong> 1 Birra Media o 1 Bevanda, 1 Acqua 1/2L, Caffè</p>

            <PriceBox>
              <p><strong>Adulto/Vegetariano:</strong> €25 (acconto €5)</p>
              <p><strong>Bambino:</strong> €15 (acconto €5)</p>
            </PriceBox>

            <SouvenirRow>
              <SouvenirImage src={glassImage} alt="Bicchiere commemorativo Aria di Fest 2026" />
              <SouvenirText>
                <h4>Mezzo litro di bontà (la tua)</h4>
                <p>Se vuoi un ricordo della serata colleziona il bicchiere Aria Di Fest 2026 — <strong>€5</strong></p>
              </SouvenirText>
            </SouvenirRow>
          </MenuCard>
        </Container>
      </MenuSection>

      <ActionSection>
        <Container>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            style={{ marginBottom: '30px', fontSize: '2.2rem' }}
          >
            Scarica e Condividi
          </motion.h2>

          <ActionButtons>
            <Button variant="primary" onClick={handleDownloadFlyer}>
              Scarica Locandina
            </Button>
            <Button variant="secondary" onClick={handleShareFlyer}>
              Condividi Evento
            </Button>
          </ActionButtons>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Button onClick={handleRegisterClick} disabled={checking}>
              {checking ? 'Controllo...' : 'Iscriviti ora'}
            </Button>
          </motion.div>

          <CharityInfo
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <p>❤ Tutto il ricavato sarà devoluto alla <a href="https://www.fibrosicisticaricerca.it/" style={{ color: colors.primary }}>Fondazione Ricerca Fibrosi Cistica</a></p>
            <a href="https://www.fibrosicisticaricerca.it/"><img src="/logo_fondazione.png" alt="Fondazione Ricerca Fibrosi Cistica" style={{ marginTop: '15px', maxWidth: '200px' }} /></a>
          </CharityInfo>
        </Container>
      </ActionSection>
    </>
  );
};
